import { useMemo, useState, useEffect } from "react";
import { DataManagementBulkButtonComponents, DataManagementTableBulkName } from "@components/common/faro-table/faro-table-types";
import { TableItem } from "@pages/data-management/data-management-types";
import { RemoveScansButton } from "@pages/data-management/remove-scans-button";
import { useAppSelector, useAppDispatch } from "@store/store-helper";
import { selectedIdsSelector } from "@store/table/table-selector";
import { useShouldShowBulkAction } from "@hooks/table/use-should-show-bulk-action";
import { CaptureTreeEntityType } from "@faro-lotv/service-wires";
import { captureTreeForOpenDraftRevisionSelector } from "@store/capture-tree/capture-tree-selectors";
import { loggedInUserSelector } from "@store/user/user-selector";
import { useAppParams } from "@router/router-helper";
import { ProcessOrbisScansButton } from "@pages/data-management/process-orbis-scans-button";
import { resetTableState } from "@store/table/table-slice";
import { isDevModeEnabledSelector } from "@store/app/app-selector";

const orbisScanTypes = [CaptureTreeEntityType.orbisFlashScan, CaptureTreeEntityType.orbisScan];

interface Props {
  /** Array of DataManagementTableItem entities. */
  items: TableItem[],
  /** Callback to set the scans the user selected for removal. */
  setScansToRemove: React.Dispatch<React.SetStateAction<TableItem[]>>;
  /** Callback to set whether the confirmation dialog for removing scans is open. */
  setIsRemoveScansDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

/** All bulk action buttons for the scan table in the Staging Area. */
export function DataManagementBulkActions({
  items,
  setScansToRemove,
  setIsRemoveScansDialogOpen,
}: Props): DataManagementBulkButtonComponents {
  const dispatch = useAppDispatch();
  const isDevModeEnabled = useAppSelector(isDevModeEnabledSelector);
  const selectedIds = useAppSelector(selectedIdsSelector("scans"));
  // fetch currently logged in user
  const loggedInUser = useAppSelector(loggedInUserSelector);
  const loggedInUserId = (loggedInUser?.userId);
  // boolean to detect if processOrbisScans button is clicked
  const [ isProcessOrbisBtnClicked, setIsProcessOrbisBtnClicked ] = useState<boolean>(false);

  useEffect(() => {
    if (isProcessOrbisBtnClicked){
      dispatch(resetTableState());
      setIsProcessOrbisBtnClicked(false);
    }
  }, [isProcessOrbisBtnClicked, dispatch]);

  // fetch project Id
  const { projectId } = useAppParams();
  const selectedItems = useMemo(() => {
    const ids = new Set(selectedIds);
    return items.filter((item) => ids.has(item.id));
  }, [selectedIds, items]);

  const allowedButtonsBasedOnRowSelection: DataManagementTableBulkName[] = useMemo(() => {
    const allowedBulkActions: DataManagementTableBulkName[] = [];
    if (selectedItems.length) {
      allowedBulkActions.push("removeScan");
    }
    // check for orbis scans so that processing-in-bulk can be enabled
    if ((selectedItems.length && !isProcessOrbisBtnClicked) &&
      selectedItems.some((item) => (orbisScanTypes.includes(item.deviceType)))){
        allowedBulkActions.push("processOrbisScan");
    }
    return allowedBulkActions;
  }, [selectedItems, isProcessOrbisBtnClicked]);

  // fetch capture tree
  const captureTreeOpenDraft = useAppSelector(captureTreeForOpenDraftRevisionSelector);
  const selectedOrbisRawPointCloudIds = useMemo(() => {
    const pointCloudIds: string[] = [];
    // filter Orbis Scans from the selected items in the table if dev-mode is enabled
    const selectedOrbisItems = isDevModeEnabled ? selectedItems.filter((item) =>
      orbisScanTypes.includes(item.deviceType)) : [];
    for (const item of selectedOrbisItems){
      const entity = captureTreeOpenDraft
        .find((entity) => entity.id === item.id);
      const pointCloudId = (entity?.pointClouds?.find((pointCloud) =>
        pointCloud.type === "OrbisRaw")?.id);
      if (pointCloudId) {
        pointCloudIds.push(pointCloudId);
      }
    }
    return pointCloudIds;
  },[captureTreeOpenDraft, selectedItems, isDevModeEnabled]);

  const { shouldShowBulkButtons } = useShouldShowBulkAction({ allowedButtonsBasedOnRowSelection });
  const shouldShowOrbisProcessButton = isDevModeEnabled && !!loggedInUserId && !!projectId && shouldShowBulkButtons("processOrbisScan");

  return {
    removeScan: shouldShowBulkButtons("removeScan") ? (
      <RemoveScansButton
        selectedItems={selectedItems}
        setScansToRemove={setScansToRemove}
        setIsRemoveScansDialogOpen={setIsRemoveScansDialogOpen}
      />
    ) : null,

    processOrbisScan: shouldShowOrbisProcessButton ? (
      <ProcessOrbisScansButton
        selectedItems={selectedOrbisRawPointCloudIds}
        setIsProcessOrbisBtnClicked={setIsProcessOrbisBtnClicked}
        userId={loggedInUserId}
        projectId={projectId}
      />
    ) : null,
  };
}
